import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import LocalizedDatePickerProvider from 'components/LocalizedDatePickerProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from 'services/query';
import { configureStore } from 'domain/store';
import intl from 'translation';
import WithDragDropContextProvider from 'lib/dndProvider';
import { getResponseInterceptors } from 'lib/apiHelpers';
import apiClientInitializer from 'initializers/apiClientInitializer';
import paymentApiClientInitializer from 'initializers/paymentApiClientInitializer';
import { registerAppSwitcherTabActivationEventHandler } from 'lib/appSwitcher';
import ConnectionHealthService from 'lib/ConnectionHealthService';
import 'moment/locale/he';
import 'moment/locale/es';
import 'moment/locale/it';
import 'moment/locale/fr';
import { registerSW } from 'serviceWorkerRegistration';
import awsAmplifyInitializer, { initCognitoEventHub } from 'initializers/awsAmplify';
import envConfigInitializer from 'initializers/envConfigInitializer';
import muiDatePickerLicense from 'initializers/muiDatePickerLicense';
import agGridLicense from 'initializers/agGridLicense';
import reactotronInitializer from 'initializers/reactotron';
import AppUninitializedEnv from 'initializers/src/AppUninitializedEnv';

import LabeledThemeProvider from 'components/LabeledThemeProvider';

import Labels from './labels';

import router from 'domain/router/router';
import rootSaga from './sagas';
import RootRoute from 'pages/RootRoute';
import { ErrorBoundary, notify } from 'lib/errorLogger';
import { CognitoAuthError } from 'lib/customErrors';

import tokenKeeper from 'lib/apiTokenKeeper';

reactotronInitializer();

const AppWithDnDContext = WithDragDropContextProvider(RootRoute);
const rootNode = document.getElementById('root');

awsAmplifyInitializer()
  .then(() => {
    envConfigInitializer()
      .then(() => {
        // !!! PAY ATTENTION - DON CHANGE ORDER store -> tokenKeeper -> runSaga
        const store = configureStore(router.navigate);

        tokenKeeper.injectStore(store);
        apiClientInitializer(store);
        paymentApiClientInitializer(store);

        store.runSaga(rootSaga, store.dispatch);

        // handles Cognito Authorization
        initCognitoEventHub(store);
        // register tab activation handler for app switcher
        registerAppSwitcherTabActivationEventHandler();
        // CONNECTION HEALTH SERVICE - detect slow connection for users
        ConnectionHealthService(store.dispatch);
        muiDatePickerLicense();
        agGridLicense();
        console.warn('App version:', process.env.REACT_APP_BUILD_VERSION);
        axios.interceptors.response.use(...getResponseInterceptors(store));

        intl(store)
          .then(({ locale, translation }) => {
            ReactDOM.render(
              <Provider store={store}>
                <QueryClientProvider client={queryClient}>
                  <IntlProvider locale={locale} defaultLocale="en" messages={translation} textComponent="span">
                    <Labels>
                      <LabeledThemeProvider>
                        <LocalizedDatePickerProvider>
                          <ErrorBoundary logger={notify.captureEvent}>
                            <AppWithDnDContext />
                          </ErrorBoundary>
                        </LocalizedDatePickerProvider>
                      </LabeledThemeProvider>
                    </Labels>
                  </IntlProvider>
                  {process.env.REACT_APP_ENV === 'local' && <ReactQueryDevtools />}
                </QueryClientProvider>
              </Provider>,
              rootNode,
            );
          })
          .catch((err) => {
            // @see https://stackoverflow.com/questions/18391212/is-it-not-possible-to-stringify-an-error-using-json-stringify
            throw new Error(
              `Error loading translation or locale ${JSON.stringify(err, Object.getOwnPropertyNames(err))}`,
            );
          });

        registerSW();
      })
      .catch((error) => {
        // in case of other CognitoAuthError we do nothing as logout saga in envConfigInitializer will
        // dispatch logout action
        if (!(error instanceof CognitoAuthError)) {
          ReactDOM.render(<AppUninitializedEnv />, document.getElementById('root'));
        }
      });
  })
  .catch(() => {
    // this renders SWW page if cognito unprotected config failed to fetch
    ReactDOM.render(<AppUninitializedEnv />, document.getElementById('root'));
  });
